import { useCallback, useRef, useState, type ReactNode } from 'react';
import { fetchUremontAuthToken } from '@/shared/api/fetchUremontAuthToken';

type Props = {
  userId: number | string;
  /** Полный URL профиля в ЛК, например SERVICE_URL/cabinet/service/profile */
  route: string;
  children: ReactNode;
};

/**
 * Переход в ЛК под пользователем: берёт token через admin API
 * и открывает ЛК с ?token=… (ЛК кладёт его в cookie).
 */
export const UserProfileRedirect = ({ userId, route, children }: Props) => {
  const formRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState(false);

  const loginAsUser = useCallback(async () => {
    if (pending) return;

    setPending(true);
    try {
      const res = await fetchUremontAuthToken(userId);
      const uremontToken = res?.token;

      if (!uremontToken || !inputRef.current || !formRef.current) {
        throw new Error('Не удалось получить токен пользователя');
      }

      inputRef.current.value = uremontToken;
      formRef.current.submit();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('UserProfileRedirect failed', error);
      window.alert('Не удалось открыть профиль пользователя');
    } finally {
      setPending(false);
    }
  }, [pending, userId]);

  return (
    <form ref={formRef} action={route} method="GET" target="_blank">
      <input ref={inputRef} name="token" type="hidden" defaultValue="" readOnly />
      <div
        role="button"
        tabIndex={0}
        style={{ cursor: pending ? 'wait' : 'pointer', display: 'inline-block' }}
        onClick={loginAsUser}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            void loginAsUser();
          }
        }}
      >
        {children}
      </div>
    </form>
  );
};
